import React, { useState, useMemo } from 'react';
import { Clock, CheckCircle2, XCircle, Loader2, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import type { AnalysisJob } from '../../types/trading';

interface Props {
  jobs: AnalysisJob[];
  total: number;
  onRetry?: (jobId: number) => void;
  retrying?: boolean;
}

function statusBadge(status: string) {
  switch (status) {
    case 'queued':
      return (
        <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-yellow-500/10 text-yellow-400">
          <Clock size={10} />
          Queued
        </span>
      );
    case 'processing':
      return (
        <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-blue-500/10 text-blue-400">
          <Loader2 size={10} className="animate-spin" />
          Processing
        </span>
      );
    case 'completed':
      return (
        <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400">
          <CheckCircle2 size={10} />
          Completed
        </span>
      );
    case 'failed':
      return (
        <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-red-500/10 text-red-400">
          <XCircle size={10} />
          Failed
        </span>
      );
    default:
      return <span className="text-xs text-slate-500">{status}</span>;
  }
}

function jobTypeLabel(jobType: string) {
  switch (jobType) {
    case 'analyze':
      return 'Auto Analysis';
    case 'confirm':
      return 'Confirmation';
    case 'analyze-manual':
      return 'Manual Analysis';
    default:
      return jobType;
  }
}

function formatDuration(seconds: number | null) {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

function formatTime(isoStr: string) {
  const d = new Date(isoStr);
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

interface ParsedResult {
  signal: string;
  confidence: number;
  confirmed?: boolean;
  entry?: number;
  stop_loss?: number;
  take_profit?: number;
  reasoning?: string;
}

function parseResultPayload(payload: string | null): ParsedResult | null {
  if (!payload) return null;
  try {
    const data = JSON.parse(payload);
    return {
      signal: data.signal || 'NONE',
      confidence: data.confidence ?? 0,
      confirmed: data.confirmed,
      entry: data.entry,
      stop_loss: data.stop_loss,
      take_profit: data.take_profit,
      reasoning: data.reasoning,
    };
  } catch {
    return null;
  }
}

function signalBadge(result: ParsedResult, jobType: string) {
  const isConfirmation = jobType === 'confirm';

  // For confirmations, show confirmed/rejected
  if (isConfirmation && result.confirmed !== undefined) {
    return result.confirmed ? (
      <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-medium">
        Confirmed {result.signal} {result.confidence}%
      </span>
    ) : (
      <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 font-medium">
        Rejected {result.confidence}%
      </span>
    );
  }

  // For analyses, show signal direction
  if (result.signal === 'BUY') {
    return (
      <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-medium">
        BUY {result.confidence}%
      </span>
    );
  }
  if (result.signal === 'SELL') {
    return (
      <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 font-medium">
        SELL {result.confidence}%
      </span>
    );
  }
  return (
    <span className="text-xs px-1.5 py-0.5 rounded bg-slate-500/15 text-slate-400 font-medium">
      HOLD {result.confidence}%
    </span>
  );
}

const AnalysisJobQueue: React.FC<Props> = ({ jobs, total, onRetry, retrying }) => {
  const [expanded, setExpanded] = useState<number | null>(null);

  const activeJobs = jobs.filter((j) => j.status === 'queued' || j.status === 'processing');
  const recentJobs = jobs.filter((j) => j.status === 'completed' || j.status === 'failed');

  // Parse result payloads once
  const parsedResults = useMemo(() => {
    const map = new Map<number, ParsedResult | null>();
    for (const job of recentJobs) {
      map.set(job.id, parseResultPayload(job.result_payload ?? null));
    }
    return map;
  }, [recentJobs]);

  if (jobs.length === 0) return null;

  return (
    <div className="space-y-2">
      {/* Active Jobs */}
      {activeJobs.length > 0 && (
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
          <div className="text-xs font-medium text-blue-400 mb-2">
            Active Jobs ({activeJobs.length})
          </div>
          {activeJobs.map((job) => (
            <div key={job.id} className="flex items-center gap-3 py-1">
              {statusBadge(job.status)}
              <span className="text-xs text-slate-500 font-mono">#{job.id}</span>
              <span className="text-sm text-white font-medium">{job.symbol}</span>
              <span className="text-xs text-slate-400">{jobTypeLabel(job.job_type)}</span>
              {job.status === 'processing' && job.started_at && (
                <span className="text-xs text-blue-300 ml-auto">
                  Started {formatTime(job.started_at)}
                </span>
              )}
              {job.status === 'queued' && (
                <span className="text-xs text-yellow-300 ml-auto">
                  Queued {formatTime(job.created_at)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Recent Completed/Failed Jobs */}
      {recentJobs.length > 0 && (
        <div className="bg-slate-800/30 border border-slate-700/30 rounded-lg p-3">
          <div className="text-xs font-medium text-slate-400 mb-2">
            Recent Jobs ({total} total)
          </div>
          <div className="space-y-1">
            {recentJobs.slice(0, 5).map((job) => {
              const result = parsedResults.get(job.id) ?? null;
              return (
                <div key={job.id}>
                  <button
                    onClick={() => setExpanded(expanded === job.id ? null : job.id)}
                    className="flex items-center gap-2 py-1 w-full text-left hover:bg-slate-700/20 rounded px-1 -mx-1"
                  >
                    {statusBadge(job.status)}
                    <span className="text-xs text-slate-500 font-mono">#{job.id}</span>
                    <span className="text-sm text-slate-300">{job.symbol}</span>
                    <span className="text-xs text-slate-500">{jobTypeLabel(job.job_type)}</span>
                    {result && signalBadge(result, job.job_type)}
                    <span className="text-xs text-slate-500 ml-auto">
                      {formatDuration(job.duration_seconds)}
                    </span>
                    <span className="text-xs text-slate-600">
                      {formatTime(job.created_at)}
                    </span>
                    {expanded === job.id ? (
                      <ChevronUp size={12} className="text-slate-500" />
                    ) : (
                      <ChevronDown size={12} className="text-slate-500" />
                    )}
                  </button>

                  {expanded === job.id && (
                    <div className="ml-6 mt-1 mb-2 text-xs space-y-1">
                      {result && result.entry && (
                        <div className="flex gap-4 text-slate-400">
                          <span>Entry: <span className="text-slate-300">{result.entry}</span></span>
                          {result.stop_loss && (
                            <span>SL: <span className="text-red-400">{result.stop_loss}</span></span>
                          )}
                          {result.take_profit && (
                            <span>TP: <span className="text-emerald-400">{result.take_profit}</span></span>
                          )}
                        </div>
                      )}
                      {result?.reasoning && (
                        <div className="text-slate-400 bg-slate-800/50 rounded p-2 line-clamp-3">
                          {result.reasoning}
                        </div>
                      )}
                      {job.analysis_id && (
                        <div className="text-slate-400">
                          Analysis ID: <span className="text-slate-300 font-mono">{job.analysis_id}</span>
                        </div>
                      )}
                      {job.error_message && (
                        <div className="text-red-400 bg-red-500/5 rounded p-2">
                          {job.error_message}
                        </div>
                      )}
                      {job.status === 'failed' && onRetry && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onRetry(job.id);
                          }}
                          disabled={retrying}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 rounded transition-colors disabled:opacity-50"
                        >
                          <RotateCcw size={12} className={retrying ? 'animate-spin' : ''} />
                          Retry
                        </button>
                      )}
                      {job.ea_name && (
                        <div className="text-slate-500">EA: {job.ea_name}</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisJobQueue;
